import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Integrations } from '@features/Settings/enums/IntegrationsEnums.ts';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbStore } from '@ui/Layout/store/useBreadcrumbStore.ts';
import { Card, CardContent, CardHeader } from '@core-ui/Components/ui/card.tsx';
import { Input } from '@core-ui/Components/ui/input.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { Avatar, AvatarFallback } from '@core-ui/Components/ui/avatar.tsx';
import { IconTrash, IconChevronDown, IconRefresh } from '@tabler/icons-react';
import {
  DeleteIntegrationAccountDialog
} from '@features/Settings/components/integrations/DeleteIntegrationAccountDialog.tsx';
import { z } from 'zod';
import { type HubspotAccountView } from '../../../../types/integrations/hubspot/hubspotTypes.ts';
import { useSessionStore } from '@features/auth';
import { useHubspotApi } from '@features/Settings/hooks/useHubspotApi.ts';
import { useNavigate } from 'react-router-dom';
import { HubspotMappingsComponent } from '@features/Settings/components/integrations/HubspotMappingsComponent.tsx';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@core-ui/Components/ui/collapsible.tsx';

export function HubspotPage (): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setItems = useBreadcrumbStore((s) => s.setItems);
  const currentUser = useSessionStore((sessionState) => sessionState.currentUser);
  const userId = String(currentUser?.userId ?? '').trim();
  const organizationId = String(currentUser?.organizationId ?? '').trim();
  const { getAllHubspotAccounts, loginToHubspot, removeHubspotAccount } = useHubspotApi();
  const [hubspotAccounts, setHubspotAccounts] = useState<HubspotAccountView[]>([]);
  const [isSearchingAccount, setIsSearchingAccount] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const creationEnabled = useRef(false);
  const editionEnabled = useRef(false);
  const deletionEnabled = useRef(false);

  const searchSchema = z.object({
    search: z.string().min(1, t('settings.integrationsPages.searchAccountError')).nullable()
  });

  const validateSearch = useCallback((field: 'search', value: string) => {
    const partial = searchSchema.pick({ [field]: true });
    const result = partial.safeParse({ [field]: value });
    setValidationError(result.success ? undefined : result.error.errors[0]?.message);

  }, [searchSchema]);

  function handleAccountSearch (event: React.FormEvent<HTMLInputElement>): void {

    let search = '';
    if (event.target !== null) {
      search = (event.target as HTMLInputElement).value;
      validateSearch('search', search);
    }

    const filteredAccounts = hubspotAccounts.filter((account) =>
      account.email.toLowerCase().includes(search.toLowerCase())
    );
    setIsSearchingAccount(search !== '');

    setHubspotAccounts(filteredAccounts);
  }

  async function handleLinkAccount (): Promise<void> {
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }
    await loginToHubspot();
  }

  async function fetchAccounts (): Promise<HubspotAccountView[]> {
    setIsSearchingAccount(false);
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }

    let accountList: HubspotAccountView[] = [];
    const accounts = await getAllHubspotAccounts(userId, organizationId);

    if (accounts.data !== undefined) {
      accountList = accounts.data.map((account) => {
        const { email, nonce, id } = account;
        return {
          id,
          email,
          nonce,
        };
      });
      setHubspotAccounts(accountList);
    }

    return accountList;
  }

  async function handleRemoveAccount (nonce: string): Promise<void> {
    if (currentUser == null) {
      throw new Error('No current user found in session store.');
    }
    const { organizationId, userId } = currentUser;
    await removeHubspotAccount(organizationId, userId, nonce);
    const accounts = await fetchAccounts();

    if (accounts === undefined || accounts.length === 0) {
      navigate('/settings/app/', { replace: true });
    }
  }

  function getUserInitials (name?: string): string {
    if (typeof name === 'string' && name.trim().length > 0) {
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join('');
    }
    return 'U';
  }

  useEffect(() => {
    setItems([
      { labelKey: 'breadcrumb.home', href: '/workspace/home' },
      { labelKey: 'breadcrumb.settings', href: '/workspace/settings/:activeTab' },  // This ensures the account tab is rendered by default.
      { labelKey: 'breadcrumb.app', href: '/workspace/settings/app' },
      { labelKey: 'breadcrumb.hubspot', href: '/workspace/settings/app/hubspot' },
    ]);
  }, [setItems]);

  useEffect(() => {
    if (!isSearchingAccount) {
      void fetchAccounts();
    }
  }, [isSearchingAccount]);

  return (
    <div className="grid gap-4">
      <header title="page-title" className="flex flex-wrap gap-2 items-center">
        <h1 className='text-xl sm:text-2xl md:text-3xl font-semibold'>
          {t('settings.integrationsPages.pageTitle', { integrationName: Integrations.Hubspot })}
        </h1>
        <Badge
          className="flex h-2 text-xs p-2 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
          {t('settings.appTab.cardStatus.connected')}
        </Badge>
      </header>
      <h2 className="text-sm md:text-base">{t('settings.integrationsPages.pageSubtitle')}</h2>

      <Collapsible defaultOpen>
        <Card className="p-6 gap-1 rounded-2xl shadow-lg border border-border bg-card text-foreground">
          <CardHeader className="items-start w-full px-0 mb-4">
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className='text-lg md:text-xl font-semibold'>{t('settings.integrationsPages.linkAccountsTitle')}</h2>
                <Badge
                  className="flex h-2 text-xs p-2 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
                  {t('settings.appTab.cardStatus.connected')}
                </Badge>
              </div>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon">
                  <IconChevronDown className="size-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </Button>
              </CollapsibleTrigger>
            </div>
            <h3 className="text-sm">{t('settings.integrationsPages.linkAccountsSubtitle', { integrationName: Integrations.Hubspot })}</h3>
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="grid pt-0 px-0">
              <section className="flex flex-col sm:flex-row w-full justify-between items-start sm:items-center gap-2 pb-2">
                <h4 className="text-sm">
                  {t('settings.integrationsPages.linkedAccounts', {
                    number: hubspotAccounts.length,
                    plural: hubspotAccounts.length > 1 ? 's' : ''
                  })}
                </h4>
              </section>

              <section className="flex flex-row items-center justify-between gap-4 mb-6">
                <div className="relative w-full sm:w-64">
                  <Input
                    className={`h-9 text-sm placeholder:text-muted-foreground text-foreground border w-full focus:outline-none focus:ring-1 ${
                      validationError !== undefined
                        ? '!border-error focus:!ring-error'
                        : 'border-input focus:ring-ring'
                    }`}
                    placeholder={t('settings.integrationsPages.searchAccountPlaceholder')}
                    onInput={handleAccountSearch}
                  />
                  {validationError != null && (
                    <p className="text-xs text-error absolute left-0 -bottom-5" data-qa-id="intent-title-error">
                      {validationError}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => {
                      void fetchAccounts();
                    }}
                  >
                    <IconRefresh className="w-4 h-4" />
                  </Button>

                  <Button
                    className="h-9"
                    onClick={() => {
                      void handleLinkAccount();
                    }}
                  >
                    {t('settings.integrationsPages.addAccount')}
                  </Button>
                </div>
              </section>

              <main title="accounts-listing" className="grid max-h-[35vh] overflow-y-auto mb-2">
                {hubspotAccounts.map((account, index) => {
                  return (
                    <div
                      key={`user-in-account-${index}`}
                      className="flex rounded-sm border border-border text-foreground px-2 py-2">
                      <div title="account-data-container" className="flex items-center w-full min-w-0">
                        <div className="flex gap-3 items-center w-full min-w-0">
                          <Avatar className="flex shrink-0 w-8 h-8 justify-center items-center">
                            <AvatarFallback
                              className="flex items-center justify-center h-8 w-8 text-foreground/90 text-md font-medium bg-muted">
                              {getUserInitials(account.email)}
                            </AvatarFallback>
                          </Avatar>

                          <p className="text-sm font-semibold truncate min-w-0">{account.email}</p>

                          <Badge
                            className="hidden sm:flex h-2 text-xs p-2 shrink-0 items-center rounded-sm font-semibold bg-[var(--success-badge)] text-[var(--success-text)] border border-[var(--success-badge-border)]">
                            {t('settings.appTab.cardStatus.connected')}
                          </Badge>
                        </div>
                      </div>
                      <div title="account-actions-box" className="flex gap-1 place-self-center shrink-0">
                        <DeleteIntegrationAccountDialog
                          trigger={
                            <i className="cursor-pointer">
                              <IconTrash className="w-4 h-4 m-0 text-destructive hover:text-muted-foreground"/>
                            </i>}
                          deleteCallback={() => {
                            void handleRemoveAccount(account.nonce);
                          }}/>
                      </div>
                    </div>
                  );
                })}
              </main>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      <Collapsible defaultOpen>
        <Card className="p-6 gap-1 rounded-2xl shadow-lg border border-border bg-card text-foreground">
          <CardHeader className="items-start w-full mb-4 px-0">
            <div className="flex items-center justify-between w-full">
              <h2 className='text-lg md:text-xl font-semibold'>
                {t('settings.integrationsPages.mappings.mappingsTitle')}
              </h2>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon">
                  <IconChevronDown className="size-4 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </Button>
              </CollapsibleTrigger>
            </div>
            <h3 className="text-sm">
              {t('settings.integrationsPages.mappings.mappingsSubtitle')}
            </h3>
          </CardHeader>

          <CollapsibleContent>
            <CardContent className="p-0">
              <HubspotMappingsComponent
                editionEnabled={editionEnabled.current}
                creationEnabled={creationEnabled.current}
                deletionEnabled={deletionEnabled.current}
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}